import { Chip, Popover, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useCalendarTypeStyle } from 'src/components/calendar/GlobalCalendar'
import { ICalendarEvent } from 'src/generated-types/calendarEvent'
import { CalendarEventTypes, CalendarPopoverState, typeLabelKey } from './calendar.constants'
import { getFormattedDate } from 'src/utils'

interface EventPreviewPopoverProps {
  state: CalendarPopoverState | null
  onClose: () => void
  onOpen: (event: ICalendarEvent, newTab: boolean) => void
  canOpen: (event: ICalendarEvent) => boolean
}

const EventPreviewPopover = ({ state, onClose, onOpen, canOpen }: EventPreviewPopoverProps) => {
  const { t } = useTranslation(['calendar', 'common'])
  const getTypeStyle = useCalendarTypeStyle()
  const style = state ? getTypeStyle(state.event.type) : null

  return (
    <Popover
      open={!!state}
      anchorReference="anchorPosition"
      anchorPosition={state?.position}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 12px 32px rgba(15, 23, 42, 0.16)' } }}
    >
      {state && style && (
        <Stack spacing={1.25} sx={{ p: 2, maxWidth: 340, borderTop: `3px solid ${style.accent}` }}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Chip
              size="small"
              label={t(typeLabelKey[state.event.type], { ns: 'calendar' })}
              sx={{ backgroundColor: style.tint, color: style.text, fontWeight: 700 }}
            />
            {state.event.type != CalendarEventTypes.Document && state.event.status && (
              <Typography variant="caption" color="text.secondary">
                {t(`status-${state.event.status.toLowerCase()}`, { ns: 'calendar', defaultValue: state.event.status })}
              </Typography>
            )}
          </Stack>
          <Typography variant="subtitle1" fontWeight={700}>
            {state.event.title}
          </Typography>
          {state.event.localityName && (
            <Typography variant="body2" color="text.secondary">
              {t('locality', { ns: 'calendar' })}: {state.event.localityName}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            {t('date', { ns: 'calendar' })}: {getFormattedDate(new Date(state.event.start))}
            {state.event.end ? ` to ${getFormattedDate(new Date(state.event.end))}` : ''}
          </Typography>
          {state.event.description && (
            <Typography variant="body2" color="text.secondary">
              {state.event.description}
            </Typography>
          )}
          {canOpen(state.event) && (
            <Chip
              label={t('open', { ns: 'calendar' })}
              color="primary"
              onClick={e => onOpen(state.event, e.ctrlKey || e.metaKey)}
              sx={{ alignSelf: 'flex-start', fontWeight: 600, cursor: 'pointer', mt: 0.5 }}
            />
          )}
        </Stack>
      )}
    </Popover>
  )
}

export default EventPreviewPopover
